import React, { useState } from "react";
import _ from "lodash";
import { IconButton } from "@metacrm/metacrm-material-ui/dist/Button";
import {
  StyledGeneralSettingsContainer,
  StyledSettingTitle,
  StyledDescription,
  StyledSettingContent,
  StyledSettingContentLabel,
  StyledSettingContentValue,
  StyledImportPopoverErrorText,
  StyledDisplayField,
} from "../OnChainDataSource.styles";
import { StyledTextButton } from "features/metadesk/campaign/campaignAnalysis/common.styles";
import Grid from "@metacrm/metacrm-material-ui/dist/Grid";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/styles";
import { useIntl } from "react-intl";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useConfirm } from "features/home/ConfirmDialogProvider";
import { getChainList } from "features/helpers/utils";
import ImportContractSelect from "features/metadesk/components/ImportList/ImportContractSelect";
import BasicModal from "features/metadesk/chat/BasicModal";

const PortfolioDisplayPreference = ({ entityName, ecosystemName, canEdit }) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const confirm = useConfirm();
  const chainList = getChainList(ecosystemName.toLowerCase());

  const isSolana = ecosystemName.toLowerCase() === "solana";

  const { control } = useFormContext();

  const {
    fields: nftFields,
    append: appendNft,
    remove: removeNft,
    update: updateNft,
  } = useFieldArray({ name: "portfolio.nfts", control });

  const {
    fields: tokenFields,
    append: appendToken,
    remove: removeToken,
    update: updateToken,
  } = useFieldArray({ name: "portfolio.tokens", control });

  const [openAddToken, setOpenAddToken] = useState("");
  const [editIndex, setEditIndex] = useState(null);
  const [uploadData, setUploadData] = useState({});
  const [error, setError] = useState("");

  const handleAddNewToken = (contract) => {
    if (!contract) return;
    if (editIndex !== null) {
      updateToken(editIndex, {
        address: contract.address,
        chainId: contract.chainId,
        name: contract.name,
        icon: contract.icon,
      });
    } else {
      appendToken({
        address: contract.address,
        chainId: contract.chainId,
        name: contract.name,
        icon: contract.icon,
      });
    }
    setUploadData({});
    setEditIndex(null);
    setOpenAddToken("");
  };
  const handleAddNewNft = (contract) => {
    if (!contract) return;

    if (editIndex !== null) {
      updateNft(editIndex, {
        address: contract.address,
        chainId: contract.chainId,
        name: contract.name,
        icon: contract.icon,
      });
    } else {
      appendNft({
        address: contract.address,
        chainId: contract.chainId,
        name: contract.name,
        icon: contract.icon,
      });
    }

    setUploadData({});
    setEditIndex(null);
    setOpenAddToken("");
  };

  const handleRemove = (func) => {
    confirm({
      title: formatMessage({
        id: "popup.removeItem.title",
      }),
      content: formatMessage({
        id: "popup.removeItem.message",
      }),
      confirmText: formatMessage({
        id: "global.btn.remove",
      }),
      cancelText: formatMessage({
        id: "global.btn.cancel",
      }),
    })
      .then(() => {
        func();
      })
      .catch(() => {});
  };

  return (
    <StyledGeneralSettingsContainer>
      <StyledSettingTitle>
        {formatMessage({ id: "dataSourceSetting.portfolioDisplay.title" })}
      </StyledSettingTitle>
      <StyledDescription>
        {formatMessage({
          id: "dataSourceSetting.portfolioDisplay.description",
        })}
      </StyledDescription>

      {/* Token Section */}
      <StyledSettingContent>
        <StyledSettingContentLabel sx={{ marginTop: "4px" }}>
          {formatMessage({ id: "global.commomWord.token" })}
        </StyledSettingContentLabel>
        <StyledSettingContentValue>
          {tokenFields.map((item, index) => {
            const chain = chainList.find((c) => c.value === item.chainId);
            return (
              <Box
                key={item.id}
                display="flex"
                alignItems="center"
                mb={1}
                position="relative"
              >
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={5}>
                    <StyledDisplayField>{chain?.label}</StyledDisplayField>
                  </Grid>
                  <Grid item xs={6}>
                    <StyledDisplayField>
                      <img src={item.icon} alt="token" />
                      {item.name}
                    </StyledDisplayField>
                  </Grid>

                  {canEdit && (
                    <Grid item xs={1}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        <IconButton
                          size={18}
                          onClick={() => {
                            setUploadData({ contract: item });
                            setEditIndex(index);
                            setOpenAddToken("token");
                          }}
                        >
                          <i className="meta-crm-icon-ic_edit-1 font-size-14" />
                        </IconButton>
                        <IconButton
                          size={18}
                          onClick={() => handleRemove(() => removeToken(index))}
                        >
                          <i className="meta-crm-icon-ic_trash font-size-14" />
                        </IconButton>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            );
          })}
          {canEdit && (
            <StyledTextButton
              onClick={() => {
                setOpenAddToken("token");
                setEditIndex(null);
              }}
            >
              <span className="plus">
                <i className="meta-crm-icon-ic_add font-size-16" />
              </span>
              {formatMessage({ id: "global.btn.add" })}
            </StyledTextButton>
          )}
        </StyledSettingContentValue>
      </StyledSettingContent>

      {/* NFT Section */}
      {!isSolana && (
        <StyledSettingContent>
          <StyledSettingContentLabel sx={{ marginTop: "4px" }}>
            {formatMessage({ id: "global.commomWord.nft" })}
          </StyledSettingContentLabel>
          <StyledSettingContentValue>
            {nftFields.map((item, index) => {
              const chain = chainList.find((c) => c.value === item.chainId);
              return (
                <Box
                  key={item.id}
                  display="flex"
                  alignItems="center"
                  mb={1}
                  position="relative"
                >
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={5}>
                      <StyledDisplayField>{chain?.label}</StyledDisplayField>
                    </Grid>
                    <Grid item xs={6}>
                      <StyledDisplayField>
                        <img src={item.icon} alt="nft" />
                        {item.name}
                      </StyledDisplayField>
                    </Grid>
                    {canEdit && (
                      <Grid item xs={1}>
                        <Box display="flex" justifyContent="center" gap="5px">
                          <IconButton
                            size={18}
                            onClick={() => {
                              setUploadData({ contract: item });
                              setEditIndex(index);
                              setOpenAddToken("nft");
                            }}
                          >
                            <i className="meta-crm-icon-ic_edit-1 font-size-14" />
                          </IconButton>
                          <IconButton
                            size={18}
                            onClick={() => handleRemove(() => removeNft(index))}
                          >
                            <i className="meta-crm-icon-ic_trash font-size-14" />
                          </IconButton>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              );
            })}
            {canEdit && (
              <StyledTextButton
                onClick={() => {
                  setUploadData({});
                  setEditIndex(null);
                  setOpenAddToken("nft");
                }}
              >
                <span className="plus">
                  <i className="meta-crm-icon-ic_add font-size-16" />
                </span>
                {formatMessage({ id: "global.btn.add" })}
              </StyledTextButton>
            )}
          </StyledSettingContentValue>
        </StyledSettingContent>
      )}

      {/* Import Contract Modal */}
      {openAddToken === "token" && (
        <BasicModal
          open
          textAlign="left"
          closeBtnAbsolute
          onClose={() => {
            setUploadData({});
            setEditIndex(null);
            setOpenAddToken("");
          }}
          onHandleConfirm={() => handleAddNewToken(uploadData.contract)}
          modalTitle={
            <Box sx={{ padding: "6px 0 12px" }}>
              {formatMessage({ id: "popup.addNewToken.title" })}
            </Box>
          }
          cancelText={formatMessage({ id: "global.btn.cancel" })}
          confirmText={formatMessage({ id: "global.btn.addToken" })}
          mode="action"
          confirmDisabled={!uploadData.contract}
          children={
            <div style={{ paddingBottom: 40, width: 440 }}>
              <ImportContractSelect
                hideTypeSelect
                entityName={entityName}
                chainList={chainList}
                uploadData={uploadData}
                setUploadData={setUploadData}
                setError={setError}
                type="token"
              />
              {error && (
                <StyledImportPopoverErrorText>
                  {error}
                </StyledImportPopoverErrorText>
              )}
            </div>
          }
        />
      )}
      {openAddToken === "nft" && (
        <BasicModal
          open
          textAlign="left"
          closeBtnAbsolute
          onClose={() => {
            setUploadData({});
            setEditIndex(null);
            setOpenAddToken(false);
          }}
          onHandleConfirm={() => handleAddNewNft(uploadData.contract)}
          modalTitle={formatMessage({ id: "popup.addNewNFT.title" })}
          cancelText={formatMessage({ id: "global.btn.cancel" })}
          confirmText={formatMessage({ id: "global.btn.addNft" })}
          confirmDisabled={!uploadData.contract}
          mode="action"
        >
          <div style={{ paddingBottom: 40, width: 440 }}>
            <ImportContractSelect
              hideTypeSelect
              entityName={entityName}
              chainList={chainList}
              uploadData={uploadData}
              setUploadData={setUploadData}
              setError={setError}
              type="nft"
            />
            {error && (
              <StyledImportPopoverErrorText>
                {error}
              </StyledImportPopoverErrorText>
            )}
          </div>
        </BasicModal>
      )}
    </StyledGeneralSettingsContainer>
  );
};

export default PortfolioDisplayPreference;
